import { motion } from "framer-motion";
import { Phone, MessageSquare, ExternalLink, AlertTriangle } from "lucide-react";
import { Eyebrow, MaskLine } from "./Reveal";
import { EASE } from "@/lib/brand";

const LINES = [
  { id: "samaritans", name: "Samaritans", action: "Call 116 123", note: "Free, any time, day or night. 365 days a year.", href: "tel:116123", icon: Phone },
  { id: "calm", name: "CALM", action: "Call 0800 58 58 58", note: "Campaign Against Living Miserably. Helpline & webchat, 5pm–midnight daily.", href: "tel:0800585858", icon: Phone },
  { id: "shout", name: "Shout", action: "Text SHOUT to 85258", note: "Free, confidential 24/7 text support if you'd rather not talk.", href: "sms:85258?&body=SHOUT", icon: MessageSquare },
  { id: "nhs111", name: "NHS 111", action: "Call 111, option 2", note: "Urgent mental health help through the NHS, 24/7.", href: "tel:111", icon: Phone },
  { id: "andys", name: "Andy's Man Club", action: "Find a group", note: "Free peer-to-peer support groups for men, across the UK.", href: "https://andysmanclub.co.uk", icon: ExternalLink },
  { id: "mind", name: "Mind", action: "Visit mind.org.uk", note: "Information, advice and local support services.", href: "https://www.mind.org.uk", icon: ExternalLink },
];

export const Support = () => (
  <section id="support" data-testid="support-section" className="relative bg-[#07090D] py-24 md:py-36 overflow-hidden">
    <img src="/images/hero.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
    <div className="absolute inset-0 bg-gradient-to-b from-[#07090D] via-[#07090D]/80 to-[#07090D]" />
    <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid gap-10 lg:grid-cols-12 mb-14">
        <div className="lg:col-span-7">
          <Eyebrow>05 — Support</Eyebrow>
          <h2 className="mt-5 font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tight">
            <MaskLine inView>You're not alone.</MaskLine>
            <MaskLine inView delay={0.1}><span className="text-[var(--mth-blue)]">Someone's listening.</span></MaskLine>
          </h2>
        </div>
        <p className="lg:col-span-5 lg:self-end text-slate-300 text-base sm:text-lg">If things feel heavy, reach out. These UK services are free, confidential, and there for men exactly like you.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {LINES.map((l, i) => (
          <motion.a
            key={l.id}
            href={l.href}
            target={l.href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            data-testid={`helpline-${l.id}-card`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: EASE, delay: i * 0.06 }}
            className="group relative flex flex-col justify-between min-h-[210px] rounded-[22px] border border-white/10 bg-[#0B0F15]/80 backdrop-blur p-7 transition-[border-color,background-color] hover:border-[var(--mth-blue)] hover:bg-[#0E1624]"
          >
            <div className="flex items-start justify-between">
              <h3 className="font-display font-extrabold text-2xl">{l.name}</h3>
              <span className="h-10 w-10 rounded-full border border-white/15 grid place-items-center transition-[background-color,transform] group-hover:bg-[var(--mth-blue)] group-hover:rotate-[-12deg]"><l.icon className="h-4 w-4" /></span>
            </div>
            <div>
              <p className="font-mono text-sm uppercase tracking-wider text-[var(--mth-blue-soft)]">{l.action}</p>
              <p className="mt-2 text-sm text-slate-300">{l.note}</p>
            </div>
          </motion.a>
        ))}
      </div>
      <div data-testid="emergency-notice" className="mt-8 flex gap-4 items-start rounded-2xl border border-red-400/30 bg-red-500/[0.07] p-6">
        <AlertTriangle className="h-5 w-5 text-red-300 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-200">If you or someone else is in immediate danger, call <a href="tel:999" data-testid="emergency-999-link" className="font-bold text-white underline underline-offset-4">999</a> or go to your nearest A&amp;E. MenTellHealth is a clothing brand, not a medical or crisis service.</p>
      </div>
    </div>
  </section>
);
